import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAttendance } from '../context/AttendanceContext';

const { FiAlertTriangle, FiPhone, FiMail, FiUser, FiCalendar, FiClock } = FiIcons;

const AbsenceTracking = ({ onNavigate }) => {
  const { getMembersNeedingOutreach, weeklyAttendance } = useAttendance();
  
  const membersNeedingOutreach = getMembersNeedingOutreach();
  const lastMeeting = weeklyAttendance[weeklyAttendance.length - 1];
  
  const getLastAttendanceDate = (memberId) => {
    for (let i = weeklyAttendance.length - 1; i >= 0; i--) {
      const meeting = weeklyAttendance[i];
      if (meeting.attendees.some(a => a.memberId === memberId)) {
        return new Date(meeting.date).toLocaleDateString();
      }
    }
    return 'Never attended';
  };
  
  const getAbsenceStatusColor = (consecutiveAbsences) => {
    if (consecutiveAbsences >= 3) return 'text-red-600';
    if (consecutiveAbsences === 2) return 'text-orange-600';
    if (consecutiveAbsences === 1) return 'text-yellow-600';
    return 'text-green-600';
  };
  
  const getAbsenceStatusBg = (consecutiveAbsences) => {
    if (consecutiveAbsences >= 3) return 'bg-red-50 border-red-200';
    if (consecutiveAbsences === 2) return 'bg-orange-50 border-orange-200';
    if (consecutiveAbsences === 1) return 'bg-yellow-50 border-yellow-200';
    return 'bg-green-50 border-green-200';
  };

  const getAbsenceStatusText = (consecutiveAbsences) => {
    if (consecutiveAbsences >= 3) return 'REMOVED';
    if (consecutiveAbsences === 2) return 'CRITICAL';
    if (consecutiveAbsences === 1) return 'AT RISK';
    return 'GOOD';
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-gray-800">Members Needing Outreach</h2>
        <p className="text-gray-600 mt-2">
          Track members with consecutive absences and reach out to them
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="card"
        >
          <div className="card-body text-center">
            <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAlertTriangle} className="text-white text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">At Risk</h3>
            <p className="text-3xl font-bold text-yellow-600">
              {membersNeedingOutreach.filter(m => m.consecutiveAbsences === 1).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">1 absence</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="card"
        >
          <div className="card-body text-center">
            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAlertTriangle} className="text-white text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Critical</h3>
            <p className="text-3xl font-bold text-orange-600">
              {membersNeedingOutreach.filter(m => m.consecutiveAbsences === 2).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">2 absences</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="card"
        >
          <div className="card-body text-center">
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUser} className="text-white text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Removed</h3>
            <p className="text-3xl font-bold text-red-600">
              {membersNeedingOutreach.filter(m => m.consecutiveAbsences >= 3).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">3+ absences</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="card"
        >
          <div className="card-body text-center">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiPhone} className="text-white text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Need Contact</h3>
            <p className="text-3xl font-bold text-blue-600">
              {membersNeedingOutreach.length}
            </p>
            <p className="text-sm text-gray-600 mt-1">Total members</p>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="card"
      >
        <div className="card-header">
          <h3 className="text-xl font-semibold flex items-center">
            <SafeIcon icon={FiAlertTriangle} className="mr-3" />
            Members Requiring Outreach ({membersNeedingOutreach.length})
          </h3>
        </div>
        <div className="card-body">
          {membersNeedingOutreach.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <SafeIcon icon={FiUser} className="mx-auto text-4xl mb-4" />
              <p className="text-lg">No members need outreach</p>
              <p className="text-sm">All members are attending regularly</p>
            </div>
          ) : (
            <div className="space-y-4">
              {membersNeedingOutreach.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`p-4 rounded-lg border ${getAbsenceStatusBg(member.consecutiveAbsences)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        member.consecutiveAbsences >= 3 ? 'bg-red-500' : 
                        member.consecutiveAbsences === 2 ? 'bg-orange-500' : 'bg-yellow-500'
                      }`}>
                        <SafeIcon icon={FiUser} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">{member.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{member.company}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <SafeIcon icon={FiMail} className="text-gray-400" />
                            <span className="text-gray-700">{member.email}</span>
                          </div>
                          {member.phone && (
                            <div className="flex items-center space-x-2">
                              <SafeIcon icon={FiPhone} className="text-gray-400" />
                              <span className="text-gray-700">{member.phone}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <SafeIcon icon={FiCalendar} className="text-gray-400" />
                            <span className="text-gray-600">
                              Last attended: {getLastAttendanceDate(member.id)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <SafeIcon icon={FiAlertTriangle} className="text-gray-400" />
                            <span className={`font-medium ${getAbsenceStatusColor(member.consecutiveAbsences)}`}>
                              {member.consecutiveAbsences} consecutive absence{member.consecutiveAbsences !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        member.consecutiveAbsences >= 3 ? 'bg-red-100 text-red-800' : 
                        member.consecutiveAbsences === 2 ? 'bg-orange-100 text-orange-800' : 
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {getAbsenceStatusText(member.consecutiveAbsences)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {lastMeeting && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="card"
        >
          <div className="card-header">
            <h3 className="text-xl font-semibold flex items-center">
              <SafeIcon icon={FiClock} className="mr-3" />
              Last Meeting Summary
            </h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-semibold text-gray-900">
                  {new Date(lastMeeting.date).toLocaleDateString()}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Attendees</p>
                <p className="font-semibold text-green-600">
                  {lastMeeting.attendees.length}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Late Arrivals</p>
                <p className="font-semibold text-orange-600">
                  {lastMeeting.attendees.filter(a => a.minutesLate > 0).length}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-6"
      >
        <h4 className="font-semibold text-blue-800 mb-3">Updated Absence Policy</h4>
        <div className="space-y-2 text-blue-700">
          <p><strong>Prize Eligibility:</strong> Perfect attendance required (no absences)</p>
          <p><strong>Outreach Levels:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><span className="text-yellow-600 font-medium">1 absence:</span> At Risk - Contact recommended</li>
            <li><span className="text-orange-600 font-medium">2 absences:</span> Critical - Immediate contact required</li>
            <li><span className="text-red-600 font-medium">3+ absences:</span> Removed from active membership</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
};

export default AbsenceTracking;